import { Badge, Button, Dialog, Div, Flex, Hr, Icon, P, Tag, Tooltip } from '@dnb/eufemia';
import { padlock_medium as PadlockMediumIcon, view as ViewIcon } from '@dnb/eufemia/icons';
import { AccessTypes } from '@portals/shared/admin/ApiDto';
import type { ApiDto } from '@portals/shared/portal/ApiDto';
import { type JSX, PropsWithChildren } from 'react';

import { QuickLook } from '@/pages/explorer/apis/QuickLook';
import ResourceCard from '@/pages/profile/apps/components/ResourceCard';
import ApiTypeBadge from '@/pages/profile/components/ApiTypeBadge';

import style from './index.module.css';

const EXTERNAL_CLASSIFICATIONS = new Set<ApiDto['classification']>(['partner', 'public']);

function visibilityReason(api: ApiDto) {
  if (api.organization && EXTERNAL_CLASSIFICATIONS.has(api.classification)) {
    return `${api.organization.name} organization`;
  }

  if (api.teams.length > 0) {
    return `${api.teams[0]!.name} team`;
  }

  return null;
}

interface ApiCardProps {
  api: ApiDto;
  selected?: boolean;
  onSelect?: (api?: ApiDto) => void;
}

function TableColumn({ title, children }: PropsWithChildren<{ title: string }>) {
  return (
    <dl className={style['Table-col']}>
      <P bottom="small" size="small">
        {title}
      </P>
      {children}
    </dl>
  );
}

export default function ApiCard({ api, onSelect, selected }: ApiCardProps): JSX.Element {
  const visibleBecause = visibilityReason(api);
  const approvalStages = ['test', 'live', 'public'].slice(0, ['test', 'live', 'public'].indexOf(api.approvalStage));

  return (
    <ResourceCard
      api={api}
      headerContent={
        <Div className={style['header-content-container']}>
          <Div className={style['TitleAndClassificationContainer']}>
            <P right="x-small" weight="bold">
              {api.name}
            </P>
            {api.stage != 'launched' && <Badge content="Draft" right="x-small" />}
            {visibleBecause && (
              <Tooltip
                hideDelay={0}
                noAnimation
                showDelay={100}
                size="large"
                targetElement={
                  // Icon must be wrapped in div, otherwise tooltip won't appear
                  <Div>
                    <Icon icon={PadlockMediumIcon} size="medium" />
                  </Div>
                }
              >
                <span>You can see this API because you are a member of the {visibleBecause}</span>
              </Tooltip>
            )}
          </Div>

          <ApiTypeBadge type={api.type} />
        </Div>
      }
    >
      <P>{api.description}</P>
      <Flex.Container justify="flex-start" wrap>
        <Flex.Item>
          <TableColumn title="Current version">
            <P size="small">{api.currentVersion ?? '-'}</P>
          </TableColumn>
        </Flex.Item>
        <Flex.Item>
          <TableColumn title="Classification">
            <Tag>{api.classification}</Tag>
          </TableColumn>
        </Flex.Item>
        <Flex.Item>
          <TableColumn title="Authentication">
            {api.accessTypes?.map((type) => (
              <Tag key={type} right="small">
                {type === AccessTypes.O_AUTH_SYSTEM_TO_SYSTEM ? 'System to System' : 'Api keys'}
              </Tag>
            ))}
          </TableColumn>
        </Flex.Item>
        <Flex.Item>
          <TableColumn title="Open environments">
            {api.approvalStage && (
              <Tag key={api.approvalStage} right="small">
                {approvalStages.length > 0 ? approvalStages : 'All environments requires approval'}
              </Tag>
            )}
          </TableColumn>
        </Flex.Item>
      </Flex.Container>

      <Hr bottom="small" />
      <Div className={style['ButtonsContainer']} top="0">
        {onSelect && (
          <Button
            icon={selected ? '' : 'chevron_right'}
            onClick={() => (selected ? onSelect() : onSelect(api))}
            variant={selected ? 'primary' : 'secondary'}
          >
            {selected ? 'Selected' : 'Select'}
          </Button>
        )}
        {api.type === 'REST' && (
          <Dialog
            maxWidth="60rem"
            triggerAttributes={{
              text: 'Quick view',
              variant: 'tertiary',
              icon: ViewIcon,
            }}
          >
            <QuickLook api={api} />
          </Dialog>
        )}
      </Div>
    </ResourceCard>
  );
}
